import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
  useTheme,
} from '@mui/material';
import { Visibility, VisibilityOff, Phone, Lock } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({
    phone: '+7',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    let { name, value } = e.target;
    
    // Для телефона - только цифры, +7 всегда в начале
    if (name === 'phone') {
      // Удаляем все символы кроме цифр и +
      value = value.replace(/[^\d+]/g, '');
      
      // Если начинается не с +7, добавляем +7
      if (!value.startsWith('+7')) {
        // Удаляем все + и цифры, оставляем только цифры
        const digits = value.replace(/\D/g, '');
        value = '+7' + digits;
      } else {
        // Если уже есть +7, оставляем только цифры после +7
        const digits = value.replace(/\D/g, '').replace(/^7/, '');
        value = '+7' + digits;
      }
      
      // Ограничиваем длину до +7 + 10 цифр
      value = value.slice(0, 12);
    }
    
    setFormData({
      ...formData,
      [name]: value,
    });
    setError('');
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^\+7\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Валидация телефона
    if (!formData.phone || formData.phone === '+7') {
      setError('Введите номер телефона');
      return;
    }

    if (!validatePhone(formData.phone)) {
      setError('Телефон должен быть в формате +79001234567');
      return;
    }

    if (!formData.password) {
      setError('Введите пароль');
      return;
    }

    setLoading(true);

    try {
      await login(formData.phone, formData.password);
      navigate('/');
    } catch (err) {
      if (err.response?.data?.status === 'pending') {
        navigate('/pending');
      } else {
        setError(err.response?.data?.message || 'Ошибка входа. Проверьте данные.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        background: isDarkMode ? '#0d1117' : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-50%',
          left: '-50%',
          width: '200%',
          height: '200%',
          background: isDarkMode ? `
            radial-gradient(circle at 20% 80%, rgba(136, 192, 208, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 80% 20%, rgba(180, 142, 173, 0.15) 0%, transparent 40%),
            radial-gradient(circle at 40% 40%, rgba(94, 129, 172, 0.1) 0%, transparent 30%),
            radial-gradient(circle at 60% 60%, rgba(163, 190, 140, 0.08) 0%, transparent 30%)
          ` : `
            radial-gradient(circle at 20% 80%, rgba(136, 192, 208, 0.3) 0%, transparent 40%),
            radial-gradient(circle at 80% 20%, rgba(180, 142, 173, 0.2) 0%, transparent 40%),
            radial-gradient(circle at 40% 40%, rgba(94, 129, 172, 0.15) 0%, transparent 30%)
          `,
          animation: 'aurora 20s ease-in-out infinite',
          '@keyframes aurora': {
            '0%, 100%': { transform: 'translate(0, 0) rotate(0deg)' },
            '25%': { transform: 'translate(2%, 2%) rotate(1deg)' },
            '50%': { transform: 'translate(0, 4%) rotate(0deg)' },
            '75%': { transform: 'translate(-2%, 2%) rotate(-1deg)' },
          },
        },
      }}
    >
      {/* Floating orbs */}
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '10%',
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: isDarkMode 
            ? 'radial-gradient(circle, rgba(136, 192, 208, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(136, 192, 208, 0.2) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'float 8s ease-in-out infinite',
          '@keyframes float': {
            '0%, 100%': { transform: 'translateY(0px) scale(1)' },
            '50%': { transform: 'translateY(-30px) scale(1.05)' },
          },
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '15%',
          right: '15%',
          width: 250,
          height: 250,
          borderRadius: '50%',
          background: isDarkMode 
            ? 'radial-gradient(circle, rgba(180, 142, 173, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(180, 142, 173, 0.15) 0%, transparent 70%)',
          filter: 'blur(40px)',
          animation: 'float 10s ease-in-out infinite 2s',
        }}
      />

      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <Card
          sx={{
            background: isDarkMode 
              ? 'rgba(22, 27, 34, 0.7)'
              : 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(40px)',
            WebkitBackdropFilter: 'blur(40px)',
            border: '1px solid',
            borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.08)',
            borderRadius: 4,
            boxShadow: isDarkMode ? `
              0 32px 64px rgba(0, 0, 0, 0.4),
              0 0 0 1px rgba(255, 255, 255, 0.03) inset,
              0 0 80px rgba(136, 192, 208, 0.1)
            ` : `
              0 32px 64px rgba(0, 0, 0, 0.12),
              0 0 0 1px rgba(0, 0, 0, 0.02) inset,
              0 0 80px rgba(136, 192, 208, 0.08)
            `,
            overflow: 'visible',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 2,
              background: 'linear-gradient(90deg, transparent, #88c0d0, #b48ead, transparent)',
              borderRadius: '4px 4px 0 0',
            },
          }}
        >
          <CardContent sx={{ p: 5 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                mb: 4,
              }}
            >
              {/* Animated Logo */}
              <Box
                sx={{
                  width: 80,
                  height: 80,
                  borderRadius: 3,
                  background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 50%, #b48ead 100%)',
                  backgroundSize: '200% 200%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  boxShadow: '0 16px 40px rgba(136, 192, 208, 0.3)',
                  animation: 'gradient-shift 4s ease infinite, float 6s ease-in-out infinite',
                  '@keyframes gradient-shift': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                  },
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 800, color: '#0d1117' }}>
                  N
                </Typography>
              </Box>
              
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #88c0d0 0%, #b48ead 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: '-0.02em',
                  mb: 1,
                }}
              >
                Nord CRM
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                Добро пожаловать! Войдите в систему
              </Typography>
            </Box>

            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3,
                  background: 'rgba(191, 97, 106, 0.15)',
                  border: '1px solid rgba(191, 97, 106, 0.3)',
                  borderRadius: 2,
                  '& .MuiAlert-icon': {
                    color: '#bf616a',
                  }
                }}
              >
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Номер телефона"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                margin="normal"
                required
                placeholder="+79001234567"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9+]*',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Phone sx={{ color: '#88c0d0' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Пароль"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                margin="normal"
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock sx={{ color: '#88c0d0' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                        sx={{ color: 'text.secondary' }}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  background: 'linear-gradient(135deg, #88c0d0 0%, #5e81ac 100%)',
                  boxShadow: '0 8px 32px rgba(136, 192, 208, 0.3)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5e81ac 0%, #b48ead 100%)',
                    boxShadow: '0 12px 40px rgba(136, 192, 208, 0.4)',
                    transform: 'translateY(-2px)',
                    '&::before': {
                      left: '100%',
                    },
                  },
                  '&:disabled': {
                    background: 'rgba(136, 192, 208, 0.3)',
                  },
                }}
              >
                {loading ? 'Вход...' : 'Войти в систему'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 3 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Нет аккаунта?{' '}
                  <Link
                    component={RouterLink}
                    to="/register"
                    sx={{ 
                      fontWeight: 600, 
                      textDecoration: 'none',
                      color: '#88c0d0',
                      '&:hover': {
                        color: '#b48ead',
                        textDecoration: 'underline',
                      }
                    }}
                  >
                    Зарегистрироваться
                  </Link>
                </Typography>
              </Box>
            </form>

          </CardContent>
        </Card>

        {/* Footer */}
        <Typography 
          variant="caption" 
          sx={{ 
            display: 'block', 
            textAlign: 'center', 
            mt: 4, 
            color: 'text.secondary',
          }}
        >
          © 2024 Nord CRM. Управление продажами нового поколения.
        </Typography>
      </Container>
    </Box>
  );
};

export default Login;
